/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Persists an edited Lot/Serial number for a single, already-saved Expense Inventory
 * sampling line item (Catalog-sourced parts only, since those are the only ones with an
 * editable Lot/Serial field) by reusing the createUpdateExpenseLineItems mutation's
 * update branch (identified by sfid/externalId), then refetches to resync display state.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/hook/useUpdateExpenseInventoryLineItemLotNumber
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import CREATE_UPDATE_EXPENSE_LINE_ITEMS from '../../../../../../graphql/mutations/createUpdateExpenseLineItems';
import { NOTIFY_TYPE, STALE_RECORD_MESSAGE } from '../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../util/optimisticLock';
import { SamplingLineItem } from '../ExpenseInventorySamplingTable';

interface UseUpdateExpenseInventoryLineItemLotNumberProps {
  expenseInventoryDetail: any;
  refetch: () => Promise<any>;
  openSnackBar: (type: string, message: string) => void;
}

const useUpdateExpenseInventoryLineItemLotNumber = ({
  expenseInventoryDetail,
  refetch,
  openSnackBar,
}: UseUpdateExpenseInventoryLineItemLotNumberProps) => {
  const [savingUniqueId, setSavingUniqueId] = useState<string | null>(null);
  const [createUpdateExpenseLineItems, { loading: loadingSaveLotNumber }] = useMutation(
    CREATE_UPDATE_EXPENSE_LINE_ITEMS
  );

  const onLotNumberSave = useCallback(
    async (item: SamplingLineItem, lotNumber: string): Promise<void> => {
      if (!item.sfid && !item.externalId) {
        return;
      }

      setSavingUniqueId(item.uniqueId);
      try {
        const result = await createUpdateExpenseLineItems({
          variables: {
            caseId: expenseInventoryDetail?.id,
            caseExternalId: expenseInventoryDetail?.caseExternalId,
            salesRepId: expenseInventoryDetail?.salesRepId,
            formType: 'New',
            lineItems: [
              {
                sfid: item.sfid,
                externalId: item.externalId,
                productId: item.productId,
                productNumber: item.productNumber,
                quantity: String(item.quantity ?? 0),
                lotNumber,
                isLotNumber: true,
                subInventoryCode: item.subInventoryCode || '',
                sourceLocation: item.sourceLocation,
              },
            ],
          },
        });

        if (result?.data?.createUpdateExpenseLineItems?.isMaxQuantityExceeded) {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            result.data.createUpdateExpenseLineItems.message ||
              'Error: The requested quantity exceeds what is available.'
          );
          return;
        }

        await refetch();
      } catch (error) {
        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            (error as Error)?.message || 'Error: Expense Inventory part could not be updated.'
          );
        }
      } finally {
        setSavingUniqueId(null);
      }
    },
    [expenseInventoryDetail, createUpdateExpenseLineItems, refetch, openSnackBar]
  );

  return { onLotNumberSave, loadingSaveLotNumber, savingUniqueId };
};

export default useUpdateExpenseInventoryLineItemLotNumber;
